import type { StyleLibrary } from "schemas/generated/project";

import * as React from "react";

import { useNextDevServer } from "@agent-ui/features/page-renderer/components/code-execution/context";
import { useSyncedSandboxStateUrl } from "@agent-ui/store/SandboxStateStoreContext";

import { normalizeRoute } from "./utils";

const MAX_RENDERS_BEFORE_ERROR = 15;

export function usePage(opts: {
  pageRoute: string;
  overrideStyleLibrary: StyleLibrary | null;
  viewingVersionId: string | null;
  forceSkipSandboxSync?: boolean;
}) {
  const {
    pageRoute,
    overrideStyleLibrary,
    viewingVersionId,
    forceSkipSandboxSync,
  } = opts;

  const initialRenderAtRef = React.useRef(Date.now());
  const [rerenderCount, setForceRerender] = React.useState(0);

  const {
    pageStatusMap,
    refreshEpoch,
    sandboxClientSideErrors,
    clearSandboxClientSideError,
  } = useNextDevServer();

  const normalizedRoute = React.useMemo(
    () => normalizeRoute(pageRoute),
    [pageRoute],
  );

  const url = useSyncedSandboxStateUrl({
    overrideStyleLibrary,
    viewingVersionId,
    forceSkip: forceSkipSandboxSync,
  });

  const pageUrl = React.useMemo(() => {
    // NOTE (Gabe 2025-08-13): If the page has an import error then we don't
    // want to render it because it will break the dev server.
    if (pageStatusMap[normalizedRoute]?.hasImportError) {
      return undefined;
    }
    return url && normalizedRoute !== undefined
      ? `${url}/${encodeURI(normalizedRoute)}`
      : undefined;
  }, [normalizedRoute, url, pageStatusMap]);

  const hasImportError =
    pageStatusMap[normalizedRoute]?.hasImportError === true;

  // Note (Nick, 2026-01-26, REPL-22167): Get client-side error for this route if it exists
  const sandboxClientSideError = sandboxClientSideErrors.get(normalizedRoute);

  const isLoading =
    !hasImportError &&
    (!pageStatusMap[normalizedRoute] ||
      // NOTE (Bobby, 2025-12-03): We only want to consider the page as loading if it has errored in the past and it's possible
      // that we've recovered. If it's already succeeded in the past we don't need to consider it loading again.
      (pageStatusMap[normalizedRoute].status !== 200 &&
        pageStatusMap[normalizedRoute].timestamp <
          initialRenderAtRef.current) ||
      (pageStatusMap[normalizedRoute]?.status === 404 &&
        rerenderCount < MAX_RENDERS_BEFORE_ERROR));

  // Note (Nick, 2026-01-26, REPL-22167): Include client-side errors in isError check.
  // Client-side errors occur when SSR passes with 200 but client rendering fails.
  const hasHttpError =
    !isLoading && pageStatusMap[normalizedRoute]?.status !== 200;
  const isError =
    hasHttpError || (!isLoading && Boolean(sandboxClientSideError));

  const incrementRerenderCount = React.useCallback(() => {
    setForceRerender((prev) => {
      return prev < MAX_RENDERS_BEFORE_ERROR ? prev + 1 : prev;
    });
  }, []);

  React.useEffect(() => {
    if (
      pageStatusMap[normalizedRoute]?.status === 404 &&
      pageStatusMap[normalizedRoute]?.timestamp > initialRenderAtRef.current
    ) {
      const timeout = setTimeout(() => {
        incrementRerenderCount();
      }, 400);

      return () => clearTimeout(timeout);
    }
  }, [pageStatusMap, normalizedRoute, incrementRerenderCount]);

  // NOTE (Bobby, 2025-10-21): Handle turbopack race condition where newly created pages
  // aren't in the dev server's page status map yet. The dev server serves an empty page
  // without providing a 404 or error. This effect checks if the page exists in the status
  // map and forces a rerender of the iframe (which triggers a new request) if it doesn't exist, allowing turbopack
  // time to detect and compile the new file.
  React.useEffect(() => {
    if (
      pageStatusMap[normalizedRoute] ||
      normalizedRoute?.startsWith("template-preview")
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      if (!pageStatusMap[normalizedRoute]) {
        incrementRerenderCount();
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, [pageStatusMap, normalizedRoute, incrementRerenderCount]);

  // Note (Nick, 2026-01-26, REPL-22167): Create a stable callback that clears the error for this route
  const clearError = React.useCallback(() => {
    clearSandboxClientSideError(normalizedRoute);
  }, [clearSandboxClientSideError, normalizedRoute]);

  return {
    pageUrl,
    isLoading,
    isError,
    iframeKey: `${rerenderCount + refreshEpoch}`,
    sandboxClientSideError,
    clearSandboxClientSideError: clearError,
  } as const;
}
